import { useEffect, useState } from "react";
import { Phone, Menu, X, LogIn } from "lucide-react";
import { BRAND } from "@/lib/brand";

const links = [
  { label: "About", href: "#about", id: "about" },
  { label: "Goals", href: "#goals", id: "goals" },
  { label: "Services", href: "#services", id: "services" },
  { label: "Reviews", href: "#testimonials", id: "testimonials" },
  { label: "Contact", href: "#contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);

      // Scroll-spy: pick the last section whose top has crossed the activation line
      const offset = 140; // navbar height + breathing room
      let current = "";
      for (const link of links) {
        const el = document.getElementById(link.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - offset <= 0) {
          current = link.id;
        }
      }
      // Near bottom of page → activate last link
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 80
      ) {
        current = links[links.length - 1].id;
      }
      setActiveId(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 glass-nav transition-all ${
        scrolled ? "py-2" : "py-3"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between gap-6">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <div className="h-11 w-11 rounded-xl overflow-hidden ring-1 ring-black/10 bg-white p-1.5">
            <img
              src={BRAND.logo}
              alt="Akshar Investments"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-xl text-[#0B1B3D] font-semibold tracking-tight">
              Akshar <span className="text-[#00A65A]">Investments</span>
            </div>
            <div className="overline text-[10px]">Your partner in wealth creation</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => {
            const isActive = activeId === l.id;
            return (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                data-active={isActive ? "true" : "false"}
                className={`text-sm font-medium transition-colors relative group ${
                  isActive
                    ? "text-[#00A65A]"
                    : "text-[#0B1B3D]/80 hover:text-[#00A65A]"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-[#00A65A] transition-all duration-300 ${
                    isActive ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://aksharinvestments.wealthmagic.in/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-login-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0B1B3D]/25 text-[#0B1B3D] text-sm font-medium hover:border-[#00A65A] hover:text-[#00A65A] hover:bg-[#00A65A]/5 transition-all"
          >
            <LogIn className="w-4 h-4" />
            <span>Login</span>
          </a>
          <a
            href={BRAND.phoneHref}
            data-testid="navbar-call-cta"
            className="hidden md:inline-flex cta-glow items-center gap-2 px-5 py-2.5 rounded-full bg-[#0B1B3D] text-white text-sm font-medium hover:bg-[#1A2F5B] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>{BRAND.phone}</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-md text-[#0B1B3D]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-black/5 bg-[#FDFBF7]/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => {
              const isActive = activeId === l.id;
              return (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className={`text-base font-medium ${
                    isActive ? "text-[#00A65A]" : "text-[#0B1B3D]"
                  }`}
                >
                  {l.label}
                </a>
              );
            })}
            <a
              href="https://aksharinvestments.wealthmagic.in/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-login-cta"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#0B1B3D]/25 text-[#0B1B3D] text-sm font-medium"
            >
              <LogIn className="w-4 h-4" /> Login
            </a>
            <a
              href={BRAND.phoneHref}
              data-testid="mobile-call-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0B1B3D] text-white text-sm font-medium"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
